<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import type { Resolved, Service } from '@/types'
import { relatedServiceLinks, serviceBreadcrumb, settings } from '@/content'
import { useContentHtml } from '@/lib/pageHtml'
import { baseHead, serviceLd, breadcrumbLd, faqLd } from '@/lib/seo'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'
import ProBadge from '@/components/ProBadge.vue'
import FaqAccordion from '@/components/FaqAccordion.vue'
import CtaProjet from '@/components/CtaProjet.vue'
import ZonesIntervention from '@/components/ZonesIntervention.vue'
import ServiceNews from '@/components/ServiceNews.vue'

const props = defineProps<{ resolved: Resolved }>()
const service = computed(() => props.resolved.service as Service)

const isPro = computed(() => service.value.section === 'pro')
const heroImage = computed(() => service.value.seo?.og_image || service.value.featured_image || '')
const crumbs = computed(() => serviceBreadcrumb(service.value))
const links = computed(() => relatedServiceLinks(service.value.path))
const shortLabel = computed(() => service.value.title)
const contentHtml = useContentHtml(service.value.path)

useHead(() => {
  const s = service.value
  const scripts = [
    serviceLd(s),
    breadcrumbLd(crumbs.value),
  ]
  if (s.faq?.length) scripts.push(faqLd(s.faq))
  return {
    ...baseHead({
      title: s.seo?.seo_title || s.title,
      description: s.seo?.seo_description || s.intro,
      canonical: s.seo?.canonical || s.path,
      index: s.seo?.robots_index !== false,
      image: heroImage.value,
    }),
    script: scripts,
  }
})
</script>

<template>
  <div class="svc" :class="{ 'svc--pro': isPro }">
    <!-- Hero split -->
    <section class="hero">
      <div class="hero__grid">
        <div class="hero__panel">
          <span class="eyebrow">{{ isPro ? 'Espace Professionnels' : 'Ateliers du Terras' }}</span>
          <h1>{{ service.h1 || service.title }}</h1>
          <p v-if="service.hero_subtitle" class="hero__sub">{{ service.hero_subtitle }}</p>
          <router-link
            :to="isPro ? '/pro/contact-professionnel/' : '/contact-2/'"
            class="btn btn--primary"
          >
            {{ isPro ? 'Parlons de votre projet professionnel' : 'Demander une étude gratuite' }}
          </router-link>
        </div>
        <div class="hero__media">
          <img
            v-if="heroImage"
            :src="heroImage"
            :alt="service.h1 || service.title"
            fetchpriority="high"
            width="720"
            height="520"
          />
          <div v-else class="hero__ph" aria-hidden="true"></div>
          <ProBadge class="hero__pro" />
        </div>
      </div>
    </section>

    <BreadcrumbBar :crumbs="crumbs" />

    <!-- Intro -->
    <section v-if="service.intro" class="section intro">
      <div class="container">
        <p class="intro__lead">{{ service.intro }}</p>
      </div>
    </section>

    <!-- Corps editorial -->
    <section class="section content" :class="{ 'no-top': service.intro }">
      <div class="container">
        <div class="prose prose--wide" v-html="contentHtml"></div>
      </div>
    </section>

    <!-- Actualites : remplace l'ancien widget WordPress (cf scripts/lib/service-news.mjs) -->
    <ServiceNews v-if="service.news_block" :service="service" />

    <!-- FAQ -->
    <FaqAccordion
      v-if="service.faq?.length"
      :items="service.faq"
      :title="`FAQ : ${shortLabel}`"
    />

    <!-- Intervention locale -->
    <section class="band">
      <div class="container band__in">
        <h2>Intervention locale à Mayenne, Laval et La Baule</h2>
        <p>
          Entreprise artisanale avec atelier intégré, nous intervenons chez les particuliers comme
          chez les professionnels, en neuf comme en rénovation. Expertise depuis {{ settings.since }}.
        </p>
      </div>
    </section>

    <CtaProjet :subject="service.title" />

    <!-- Maillage interne -->
    <section v-if="links.length" class="section links">
      <div class="container">
        <span class="eyebrow">Nos prestations associées</span>
        <h2>Découvrez aussi</h2>
        <ul class="links__grid">
          <li v-for="l in links" :key="l.path">
            <router-link :to="l.path">{{ l.label }}<span aria-hidden="true">→</span></router-link>
          </li>
        </ul>
      </div>
    </section>

    <ZonesIntervention />
  </div>
</template>

<style scoped>
.hero {
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
}
.hero__grid {
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  align-items: stretch;
  max-width: 1440px;
  margin: 0 auto;
}
.hero__panel {
  padding: 56px clamp(20px, 4vw, 64px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}
.hero__panel h1 {
  margin: 8px 0 14px;
}
.hero__sub {
  color: var(--ink);
  font-size: 1.05rem;
  margin-bottom: 24px;
}
.hero__media {
  position: relative;
  min-height: 320px;
}
.hero__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hero__ph {
  width: 100%;
  height: 100%;
  min-height: 320px;
  background: linear-gradient(120deg, #e7eccf, #cdd9a8);
}
.hero__pro {
  position: absolute;
  top: 18px;
  right: 18px;
}

.intro__lead {
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--title);
  max-width: 860px;
  margin: 0;
  font-weight: 500;
}
.content.no-top {
  padding-top: 8px;
}

.band {
  background: var(--title);
  color: #fff;
}
.band__in {
  padding-top: 40px;
  padding-bottom: 40px;
  text-align: center;
}
.band__in h2 {
  color: #fff;
}
.band__in p {
  max-width: 760px;
  margin: 0 auto;
  color: #dfe1e3;
}

.links__grid {
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.links__grid a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 18px;
  background: #fff;
  border: 1px solid var(--border);
  border-left: 3px solid var(--green);
  border-radius: var(--radius-sm);
  color: var(--title);
  font-weight: 600;
  font-size: 0.92rem;
}
.links__grid a:hover {
  background: var(--green-soft);
  text-decoration: none;
}
.links__grid span {
  color: var(--green);
}

@media (max-width: 860px) {
  .hero__grid {
    grid-template-columns: 1fr;
  }
  .hero__media {
    min-height: 260px;
    order: -1;
  }
  .hero__panel {
    padding: 32px 20px;
  }
  .links__grid {
    grid-template-columns: 1fr;
  }
}

/* ===== Theme sombre PRO ===== */
.svc--pro {
  background: #272a23;
  color: #e8ebe0;
}
.svc--pro .hero {
  background: linear-gradient(120deg, #1c1e18, #2f332b);
  border-bottom-color: #3a3e33;
}
.svc--pro .hero__panel .eyebrow {
  color: var(--green);
}
.svc--pro .hero__panel h1 {
  color: #ffffff;
}
.svc--pro .hero__sub {
  color: #d6dbcb;
}
.svc--pro .hero__ph {
  background: linear-gradient(120deg, #2f332b, #3a3e33);
}
/* Fil d'ariane sombre */
.svc--pro :deep(.crumb) {
  background: #1c1e18;
  border-bottom-color: #3a3e33;
}
.svc--pro :deep(.crumb__link) {
  color: #a7ac9c;
}
.svc--pro :deep(.crumb__current) {
  color: #f0f2ea;
}

.svc--pro .intro,
.svc--pro .content {
  background: #272a23;
}
.svc--pro .intro__lead {
  color: #ffffff;
}
/* Contenu editorial (v-html) sur fond charbon */
.svc--pro :deep(.prose) {
  color: #d6dbcb;
}
.svc--pro :deep(.prose h2),
.svc--pro :deep(.prose h3),
.svc--pro :deep(.prose h4) {
  color: #ffffff;
}
.svc--pro :deep(.prose a) {
  color: var(--green);
}
.svc--pro :deep(.prose th),
.svc--pro :deep(.prose td) {
  border-color: #3a3e33;
}
.svc--pro :deep(.prose blockquote) {
  color: #b7bdac;
}

/* Bande "intervention locale" : encore plus sombre pour se demarquer */
.svc--pro .band {
  background: #1c1e18;
}
.svc--pro .band__in p {
  color: #c4c9ba;
}

/* Cartes de maillage sombres */
.svc--pro .links {
  background: #272a23;
}
.svc--pro .links .eyebrow {
  color: var(--green);
}
.svc--pro .links h2 {
  color: #ffffff;
}
.svc--pro .links__grid a {
  background: #2f332b;
  border-color: #3a3e33;
  border-left-color: var(--green);
  color: #f0f2ea;
}
.svc--pro .links__grid a:hover {
  background: #363b2f;
}

/* FAQ, CTA et zones : ilots clairs lisibles sur le fond sombre (contraste fort) */
.svc--pro :deep(.news .eyebrow) {
  color: var(--green);
}
.svc--pro :deep(.news h2) {
  color: #ffffff;
}
.svc--pro :deep(.news .btn--outline) {
  color: var(--green);
}
.svc--pro :deep(.news .btn--outline:hover) {
  color: #272a23;
}
.svc--pro :deep(.zones) {
  background: #2f332b;
}
.svc--pro :deep(.zones .eyebrow) {
  color: var(--green);
}
.svc--pro :deep(.zones h2) {
  color: #ffffff;
}
</style>
